/**
 * PartsSection — the PARTS block of the EnvironmentInspector (ENG-1132).
 *
 * An environment can be several splats rather than one: the furniture as its
 * own file, separate from the walls. This is where you add them, say what each
 * one IS (a label and tags), nudge its transform, and turn one into an anchor
 * the character can be told to walk to.
 *
 * ── Two save behaviours, deliberately ───────────────────────────────────────
 * The metadata fields follow the repo's explicit-save rule — dirty until Save,
 * with a DirtyBadge next to the thing it describes. Add and Remove do NOT: they
 * move bytes, and an upload sitting pending would leave a file uploaded that
 * the row does not mention. The store's header has the full reasoning.
 *
 * ── Tags here are the point of the feature ──────────────────────────────────
 * A tag on the environment ROW (the TAGS row in the shared inspector chrome)
 * classifies the whole room. These tag the SPLAT — `furniture` on the sofa,
 * `walls` on the shell — which is what makes "the furniture" something a
 * character can be asked about. Same spelling rule, deliberately: one
 * vocabulary, checked by the one validator (assetTags).
 *
 * ── "Make an anchor" is an action, never automatic ──────────────────────────
 * A part has a position, so it is tempting to let it BE a walkable target. It
 * must not be: the part's origin is inside the sofa, not a place to stand, and
 * an anchor's value to the LLM is its prose label. So this offers a one-click
 * mint that writes a REAL anchor — seeded from the part's origin and label, for
 * a human to adjust — rather than a second, implicit source of walkable names
 * that convorcher would never see.
 */
import React, { useEffect, useRef, useState } from 'react'
import DirtyBadge from '../DirtyBadge'
import { environmentPartsStore } from '../../lib/environmentPartsStore'
import { environmentAnchorStore } from '../../lib/environmentAnchorStore'
import { partLabelError, partList, partOrigin, BASE_PART_NAME } from '../../lib/environmentParts'
import { suggestName } from '../../lib/environmentAnchors'
import { tagError, withTag, withoutTag } from '../../lib/assetTags'

const AXES = [['px', 'X'], ['py', 'Y'], ['pz', 'Z']]
// Radians, spelled the way the environment's own ROTATION (rx / ry / rz) is —
// a part rotated in degrees inside a room registered in radians reads as a
// mis-scanned scene rather than as a unit mismatch (ENG-1175).
const ROT_AXES = [['rx', 'X'], ['ry', 'Y'], ['rz', 'Z']]
const SPLAT_ACCEPT = '.ply,.spz,.splat,.ksplat'

export default function PartsSection({ assetId, assetName, C }) {
  const [snap, setSnap] = useState(() => environmentPartsStore.getState())
  // Half-typed values live here, not in the store, for the reason
  // AnchorsSection gives: a partial label is not a commit, and pushing each
  // keystroke through would mark the panel dirty on a field being cleared.
  const [draftLabels, setDraftLabels] = useState({})
  const [draftTags, setDraftTags] = useState({})
  const [tagErrors, setTagErrors] = useState({})
  const fileRef = useRef(null)

  useEffect(() => environmentPartsStore.subscribe(() => {
    setSnap(environmentPartsStore.getState())
  }), [])

  const isSubject = !!assetId && snap.assetId === assetId
  const list = isSubject ? partList(snap.parts) : []
  const busy = !!snap.busy
  const dirty = snap.state === 'dirty'

  const commitLabel = (name, value) => {
    setDraftLabels(d => { const n = { ...d }; delete n[name]; return n })
    if ((snap.parts[name]?.label ?? '') === value.trim()) return
    environmentPartsStore.setField(assetId, name, 'label', value.trim())
  }

  const addTag = (name) => {
    const tags = snap.parts[name]?.tags ?? []
    const raw = draftTags[name] ?? ''
    const err = tagError(raw, tags)
    if (err) { setTagErrors(e => ({ ...e, [name]: err })); return }
    environmentPartsStore.setField(assetId, name, 'tags', withTag(tags, raw))
    setDraftTags(d => ({ ...d, [name]: '' }))
    setTagErrors(e => ({ ...e, [name]: null }))
  }

  const setAxis = (name, key, value) => {
    const n = Number(value)
    // A cleared or non-numeric field leaves the previous value on screen rather
    // than writing 0. Unlike an anchor a part at the origin is legitimate, so
    // the refusal here is about the FIELD being mid-edit, not about the value.
    if (!Number.isFinite(n)) return
    const pose = { ...(snap.parts[name]?.pose ?? {}), [key]: n }
    environmentPartsStore.setField(assetId, name, 'pose', pose)
  }

  // Scale is refused at the keystroke rather than written when it is zero or
  // negative: normalizePose coerces a non-positive scale back to 1, so writing
  // one would leave the part on screen at its old size with nothing saying why.
  const setScale = (name, value) => {
    const n = Number(value)
    if (!Number.isFinite(n) || n <= 0) return
    const pose = { ...(snap.parts[name]?.pose ?? {}), scale: n }
    environmentPartsStore.setField(assetId, name, 'pose', pose)
  }

  const makeAnchor = (part) => {
    const [px, py, pz] = partOrigin(part)
    const name = suggestName(part.name, environmentAnchorStore.getState().anchors)
    environmentAnchorStore.place(assetId, name, {
      px, py, pz,
      label: part.label || `the ${part.name.replace(/_/g, ' ')}`,
    })
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, padding: '8px 0 4px', letterSpacing: '.08em' }}>
        PARTS
        <DirtyBadge state={isSubject ? snap.state : 'clean'} message={snap.error ?? undefined} />
      </div>

      {!isSubject && (
        <div data-testid="parts-not-subject" style={{ fontSize: 11, color: C.dim, lineHeight: 1.4 }}>
          {snap.assetId
            ? `Parts are being edited on ${snap.assetName ?? 'another environment'}. Apply this one to edit its parts.`
            : 'Apply this environment to edit its parts.'}
        </div>
      )}

      {isSubject && (
        <>
          <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.4, marginBottom: 6 }}>
            The splats this environment is made of. Tag them — <code>furniture</code>,{' '}
            <code>walls</code> — so a character knows what it is looking at.
          </div>

          {list.length === 0 && (
            <div data-testid="parts-empty" style={{ fontSize: 11, color: C.dim, fontStyle: 'italic' }}>
              One splat. Add another to split this environment into parts.
            </div>
          )}

          {list.map((part) => {
            const isBase = part.name === BASE_PART_NAME
            return (
              <div key={part.name} data-testid={`part-row-${part.name}`} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {/* The name is NOT editable here. Renaming a part means
                      rewriting the key its tags, transform and any anchor made
                      from it are filed under, and the server's own upload path
                      derives it from the filename — two places minting names is
                      how they come to disagree. Re-upload under the name you
                      want. */}
                  <span data-testid={`part-name-${part.name}`} style={{ flex: 1, fontFamily: 'var(--m-mono)', fontSize: 11 }}>
                    {part.name}
                  </span>
                  <span style={{ fontSize: 9.5, color: C.dim, fontFamily: 'var(--m-mono)' }}>
                    {part.format || '?'}{part.byteSize ? ` · ${fmtSize(part.byteSize)}` : ''}
                  </span>
                  <button
                    data-testid={`part-anchor-${part.name}`}
                    onClick={() => makeAnchor(part)}
                    title={`Add an anchor at "${part.name}" so the character can be asked to walk to it`}
                    style={smallBtn}
                  >⚑</button>
                  {/* Shown and DISABLED for the environment's own file, with the
                      reason — hiding it would leave the one part that cannot be
                      removed looking identical to the ones that can. */}
                  <button
                    data-testid={`part-delete-${part.name}`}
                    onClick={() => environmentPartsStore.removePart(assetId, part.name)}
                    disabled={isBase || busy}
                    title={isBase
                      ? "This is the environment's own file — delete the environment instead, or replace its file"
                      : `Remove "${part.name}" and delete its file`}
                    style={{ ...smallBtn, opacity: isBase || busy ? 0.4 : 1 }}
                  >✕</button>
                </div>

                <input
                  data-testid={`part-label-${part.name}`}
                  value={draftLabels[part.name] ?? part.label ?? ''}
                  placeholder="description (optional) — e.g. the big grey sofa"
                  onChange={e => setDraftLabels(d => ({ ...d, [part.name]: e.target.value }))}
                  onBlur={e => commitLabel(part.name, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                  style={{ ...labelInput, borderColor: partLabelError(draftLabels[part.name]) ? C.danger : C.border }}
                />

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4, alignItems: 'center' }}>
                  {(part.tags ?? []).map(tag => (
                    <span key={tag} data-testid={`part-tag-${part.name}-${tag}`} style={chip}>
                      {tag}
                      <button
                        onClick={() => environmentPartsStore.setField(assetId, part.name, 'tags', withoutTag(part.tags, tag))}
                        title={`Remove "${tag}"`}
                        style={chipX}
                      >×</button>
                    </span>
                  ))}
                  <input
                    data-testid={`part-tag-input-${part.name}`}
                    value={draftTags[part.name] ?? ''}
                    placeholder="+ tag"
                    onChange={e => { setDraftTags(d => ({ ...d, [part.name]: e.target.value })); setTagErrors(x => ({ ...x, [part.name]: null })) }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(part.name) } }}
                    onBlur={() => { if ((draftTags[part.name] ?? '').trim()) addTag(part.name) }}
                    style={{ ...tagInput, borderColor: tagErrors[part.name] ? C.danger : C.border }}
                  />
                </div>
                {tagErrors[part.name] && (
                  <div data-testid={`part-tag-error-${part.name}`} style={{ fontSize: 10, color: C.danger, marginTop: 2 }}>
                    {tagErrors[part.name]}
                  </div>
                )}

                {/* POSE / ROT / SCALE, the same three the environment itself
                    has. Every one of them already travelled the whole way —
                    serializeParts writes them, partMounts hands them to the
                    renderer, and the bake composites by them — so a part could
                    be rotated and scaled by everything except a person. */}
                {[['POS', AXES], ['ROT', ROT_AXES]].map(([rowLabel, axes]) => (
                  <div key={rowLabel} style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
                    <span style={{ ...rowLabelStyle, color: C.dim }}>{rowLabel}</span>
                    {axes.map(([key, axis]) => (
                      <label key={key} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ fontSize: 9, color: C.dim }}>{axis}</span>
                        <input
                          data-testid={`part-${part.name}-${key}`}
                          type="number"
                          step={0.01}
                          value={part.pose?.[key] ?? 0}
                          onChange={e => setAxis(part.name, key, e.target.value)}
                          style={numInput}
                        />
                      </label>
                    ))}
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
                  <span style={{ ...rowLabelStyle, color: C.dim }}>SCALE</span>
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <input
                      data-testid={`part-${part.name}-scale`}
                      type="number"
                      step={0.01}
                      min={0.01}
                      value={part.pose?.scale ?? 1}
                      onChange={e => setScale(part.name, e.target.value)}
                      style={numInput}
                    />
                  </label>
                  <span style={{ flex: 2, fontSize: 9, color: C.dim }}>rotation in radians</span>
                </div>
              </div>
            )
          })}

          {snap.dropped.length > 0 && (
            // Named, never silently skipped: the server validates everything it
            // stores, so the only way to get one of these is a hand-edited row
            // — or a part whose URL the server did not resolve, which this
            // client must not guess at (ENG-999).
            <div data-testid="parts-dropped" style={{ fontSize: 10, color: C.danger, marginTop: 4 }}>
              Unreadable and not shown: {snap.dropped.join(', ')}
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept={SPLAT_ACCEPT}
            multiple
            style={{ display: 'none' }}
            onChange={async (e) => {
              const files = [...(e.target.files ?? [])]
              e.target.value = '' // so the same file can be picked twice
              if (files.length) await environmentPartsStore.addFiles(assetId, files)
            }}
          />

          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              data-testid="parts-add"
              onClick={() => fileRef.current?.click()}
              disabled={busy || dirty}
              title={dirty
                ? 'Save or revert the edits first — adding a part replaces the whole list'
                : 'Add one or more splats as parts of this environment'}
              style={{ ...saveBtn, opacity: busy || dirty ? 0.4 : 1 }}
            >{snap.busy === 'adding' ? 'Uploading…' : 'Add splats…'}</button>
            <button
              data-testid="parts-save"
              onClick={() => environmentPartsStore.save(assetId)}
              disabled={!dirty || snap.state === 'saving'}
              style={{ ...saveBtn, opacity: !dirty ? 0.4 : 1 }}
            >{snap.state === 'saving' ? 'Saving…' : 'Save parts'}</button>
            <button
              data-testid="parts-revert"
              onClick={() => environmentPartsStore.revert()}
              disabled={!dirty}
              style={{ ...revertBtn, opacity: !dirty ? 0.4 : 1 }}
            >Revert</button>
          </div>
          {snap.error && (
            <div data-testid="parts-error" style={{ fontSize: 10, color: C.danger, marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {snap.error}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function fmtSize(bytes) {
  if (!bytes) return ''
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`
}

const labelInput = {
  width: '100%', boxSizing: 'border-box', marginTop: 3, padding: '3px 6px',
  background: 'var(--m-bg)', border: '1px solid', borderRadius: 3, color: 'inherit',
  fontFamily: 'var(--m-font)', fontSize: 10.5,
}
const tagInput = {
  width: 70, padding: '2px 5px', background: 'var(--m-bg)', border: '1px solid',
  borderRadius: 3, color: 'inherit', fontFamily: 'var(--m-mono)', fontSize: 10,
}
const numInput = {
  flex: 1, minWidth: 0, padding: '2px 4px', background: 'var(--m-bg)',
  border: '1px solid var(--m-line)', borderRadius: 3, color: 'inherit',
  fontFamily: 'var(--m-mono)', fontSize: 10,
}
// Fixed width so POS / ROT / SCALE stack into a column and their inputs line up.
const rowLabelStyle = {
  width: 30, flexShrink: 0, fontSize: 9, letterSpacing: '.06em',
}
const chip = {
  display: 'inline-flex', alignItems: 'center', gap: 2, padding: '1px 4px',
  background: 'var(--m-accent-soft)', color: 'var(--m-accent)',
  border: '1px solid var(--m-accent)', borderRadius: 3,
  fontFamily: 'var(--m-mono)', fontSize: 10,
}
const chipX = {
  background: 'none', border: 'none', color: 'inherit', cursor: 'pointer',
  padding: 0, fontSize: 11, lineHeight: 1,
}
const smallBtn = {
  background: 'none', border: '1px solid var(--m-line)', borderRadius: 3,
  color: 'var(--m-fg-3)', cursor: 'pointer', padding: '2px 6px', fontSize: 10,
}
const saveBtn = {
  flex: 1, background: 'var(--m-accent-soft)', color: 'var(--m-accent)',
  border: '1px solid var(--m-accent)', borderRadius: 3, padding: '5px 8px',
  fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer',
}
const revertBtn = {
  background: 'none', border: '1px solid var(--m-line)', borderRadius: 3,
  color: 'inherit', padding: '5px 8px', fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer',
}
